import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { getMediaUrl, useGetTopContributors } from '@onesource/services';
import type { FC } from 'react';

import { TopContributorsWidgetSkeleton } from './top-contributors-widget-skeleton';
import { WidgetCard, WidgetEmpty } from './widget-card';

interface TopContributorsWidgetProps {
  spaceId: string;
}

/**
 * Members with the most replies in the space this month. Always renders the
 * card; shows an empty state when no one has replied yet.
 */
export const TopContributorsWidget: FC<TopContributorsWidgetProps> = ({ spaceId }) => {
  const { data, isLoading } = useGetTopContributors(spaceId);

  if (isLoading) {
    return <TopContributorsWidgetSkeleton />;
  }

  const contributors = data?.contributors ?? [];
  if (contributors.length === 0) {
    return (
      <WidgetCard title="Top Contributors">
        <WidgetEmpty>No contributors yet — be the first to reply to a post.</WidgetEmpty>
      </WidgetCard>
    );
  }

  return (
    <WidgetCard title="Top Contributors">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {contributors.map(contributor => (
          <Box key={contributor.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar src={getMediaUrl(contributor.avatar)} alt={contributor.name} sx={{ width: 28, height: 28 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{ fontFamily: 'Figtree, sans-serif', fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}
              >
                {contributor.name}
              </Typography>
              <Typography sx={{ fontFamily: 'Figtree, sans-serif', fontSize: 11, color: '#808080' }}>
                {contributor.replyCount} {contributor.replyCount === 1 ? 'reply' : 'replies'} this month
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </WidgetCard>
  );
};
